/**
 * The referee-signal channel. (§5.6)
 *
 * WHY this file exists. Everything needed to SHOW a referee decision already
 * existed on both ends and nothing connected them:
 *
 *   • `sim/rules/fsm.ts` raises a `WHISTLE` RuleEvent on every staged restart's
 *     Setup→Ready flip — the exact tick the ceremony ends and the taker first
 *     becomes allowed to play the ball (owner: *"the ball is in game only after
 *     refree whisely … players has to wait for the whistle"*). It carries the
 *     restart `phase`, the taker and the spot.
 *   • `sim/rules/fouls.ts` used to apply the card a severity warranted, and
 *     this module derived WHICH card from `RuleEvent.severity` (ui/banners.ts
 *     did the same). Cards are disabled now (owner, 2026-08-17) — see
 *     `cardForSeverity` below, kept as a permanent no-op rather than deleted.
 *   • `render/referee.ts` draws the officials and positions them by Law 5, and
 *     its own header records the gap verbatim: *"There is NO whistle / card /
 *     signal gesture, because the seam carries no foul/advantage/card signal to
 *     trigger one off … If the setter is ever enriched with a referee-signal
 *     (the documented D9-style move), the action overlay hook here is ready."*
 *
 * This module IS that enrichment, and it is deliberately a separate file from
 * both renderer.ts and referee.ts so the interesting part is testable: `three`
 * cannot be meaningfully driven under vitest (no WebGL in node), but the
 * decision — *what did the referee just signal, and for how long* — is pure
 * arithmetic over a real `StepResult.events` array. Same split audio.ts uses
 * (`decideAudioFrame` pure + `SynthEngine` untestable).
 *
 * ── STRICTLY DOWNSTREAM (§15.2) ──────────────────────────────────────────────
 * Nothing here writes to a WorldState. It READS an already-produced
 * `StepResult.events` array and produces presentation state. A signal cannot
 * change the match, exactly as the WHISTLE event itself cannot (types.ts: it is
 * "an announcement of a transition the FSM makes either way").
 *
 * ── NOT character animation ──────────────────────────────────────────────────
 * The owner's standing instruction is to leave character animation alone. So
 * this drives POSITION (the referee jogs to the spot he is signalling) plus a
 * flat card plane and a whistle pulse — never a rig pose, never an arm raise.
 */

import { FoulSeverity, MatchPhase, type RuleEvent } from '@fg/shared/sim';

/**
 * What the referee is showing right now.
 *
 * The numbers are the WIRE contract too: `shared/net/ruleevent.ts` encodes
 * exactly these values in its `kind` field so online and offline drive the same
 * channel with the same integers. `refSignal.test.ts` asserts the two agree —
 * if you renumber one, that test fails rather than the game silently showing a
 * red card for a throw-in.
 */
export const enum RefSignalKind {
  None = 0,
  Whistle = 1,
  Yellow = 2,
  Red = 3,
}

/** The raised-signal payload: what happened, when, and where. */
export interface RefSignalRaise {
  kind: RefSignalKind;
  /** The SIM tick it happened on — identity, so a consumer can tell a NEW
   *  signal from the one it is already showing. */
  tick: number;
  /** The spot the referee is signalling: restart spot, or the foul's location. */
  x: number;
  z: number;
  /** Which restart is being whistled. `Warmup` when not a whistle. */
  phase: MatchPhase;
}

/** The live channel `RenderState` carries. Allocated once, mutated in place. */
export interface RefSignal extends RefSignalRaise {
  /** Seconds since the signal was raised. Advanced by `ageRefSignal`. */
  ageS: number;
  /** Seconds this signal stays up before the channel clears ITSELF. A rule
   *  event is an EDGE — it is pushed once and never re-fed — so without a
   *  self-clearing hold a stale signal would linger on screen forever. */
  holdS: number;
}

// ---------------------------------------------------------------------------
// Timings (presentation-only, so they live here rather than in the sim's
// constants file — same convention as vfx.ts's TRAIL_/SPRAY_ and audio.ts's
// CROWD_/ROAR_ tunables).
// ---------------------------------------------------------------------------

/** A whistle holds the referee at the spot roughly as long as the Ready window
 *  it opens, so he is standing over the restart while the taker takes it. */
export const WHISTLE_HOLD_S = 2.5;
/** The whistle PULSE itself is a pop, far shorter than the ceremony hold. */
export const WHISTLE_FLASH_S = 0.45;
/** A card is the biggest read in the file — held long enough to be unmissable. */
export const CARD_HOLD_S = 3;
/** Card plane fade in/out, seconds. Snappy in, gentler out. */
const CARD_FADE_IN_S = 0.12;
const CARD_FADE_OUT_S = 0.4;

export function createRefSignal(): RefSignal {
  return {
    kind: RefSignalKind.None,
    tick: -1,
    x: 0,
    z: 0,
    phase: MatchPhase.Warmup,
    ageS: 0,
    holdS: 0,
  };
}

/** How long a given kind stays up. */
export function holdForKind(kind: RefSignalKind): number {
  return kind === RefSignalKind.Whistle ? WHISTLE_HOLD_S : kind === RefSignalKind.None ? 0 : CARD_HOLD_S;
}

/**
 * CARDS ARE DISABLED (owner, 2026-08-17: "remove the red card thing and
 * yellow card from the game give only fouls"). This used to mirror
 * `rules/fouls.ts applyCard` and show a card plane for Reckless/Excessive/
 * DOGSO; `applyCard` itself is now a permanent no-op, and this function
 * follows it — no severity raises a card any more, only a WHISTLE does
 * (`scanRefSignal` below still raises that for every restart, carded or
 * not). Kept as a named function, not deleted, so `scanRefSignal`'s call
 * site and every `_severity`-shaped caller stay untouched.
 */
export function cardForSeverity(_severity: FoulSeverity | undefined): RefSignalKind {
  return RefSignalKind.None;
}

/** Bigger wins: a card outranks a whistle, red outranks yellow. */
function priority(kind: RefSignalKind): number {
  return kind as number;
}

/**
 * The ONE conversion point from the wire's `RefSignalWire` (shared/net/
 * refsignal.ts) to this module's `RefSignalKind`. The two enums use identical
 * integers on purpose — online and offline must drive one channel — so this is a
 * validating pass-through, not a translation table: anything outside the three
 * real signals becomes `None` and is ignored rather than trusted.
 *
 * Kept as a named function anyway so there is exactly one place to look if the
 * wire ever grows a signal the render layer does not draw yet. `refSignal.test.ts`
 * asserts the two numberings still agree.
 */
export function refSignalKindFromWire(kind: number): RefSignalKind {
  if (kind === RefSignalKind.Whistle) return RefSignalKind.Whistle;
  if (kind === RefSignalKind.Yellow) return RefSignalKind.Yellow;
  if (kind === RefSignalKind.Red) return RefSignalKind.Red;
  return RefSignalKind.None;
}

/**
 * The FoulSeverity that a card kind implies — the faithful inverse of
 * `cardForSeverity`, for the ONLINE path only.
 *
 * The wire frame carries the CARD; `ui/banners.ts` and `game/audio.ts` both
 * take rule events keyed by severity. Rather than teach either of them a
 * second vocabulary, an online signal is turned back into the event shape
 * they already understand. With cards disabled, `room.ts` never broadcasts
 * `RefSignalWire.Yellow`/`Red` any more — this only ever sees `Whistle` or
 * `None` in practice — but the mapping is kept intact rather than deleted.
 */
export function severityForCard(kind: RefSignalKind): FoulSeverity {
  if (kind === RefSignalKind.Red) return FoulSeverity.Excessive;
  if (kind === RefSignalKind.Yellow) return FoulSeverity.Reckless;
  return FoulSeverity.Careless;
}

/**
 * Scan ONE TICK's rule events for the strongest signal the referee should show,
 * writing it into `out`. Returns false and leaves `out` untouched when the tick
 * raised nothing visible — so a caller can safely pass its live scratch.
 *
 * Allocation-free (§15.7): iterates the array the sim already owns.
 *
 * `FOUL`, `PUNCH` and `KICK` all still route through `cardForSeverity` — and
 * with cards disabled that always answers `None`, so none of the three ever
 * raises a card plane any more. A restart still gets its whistle from the
 * separate `WHISTLE` event `fsm.ts` raises regardless of card status.
 */
export function scanRefSignal(events: readonly RuleEvent[], out: RefSignalRaise): boolean {
  let bestKind = RefSignalKind.None;
  let bestTick = -1;
  let bestX = 0;
  let bestZ = 0;
  let bestPhase = MatchPhase.Warmup;

  for (let i = 0; i < events.length; i++) {
    const e = events[i]!;
    let kind = RefSignalKind.None;
    if (e.type === 'WHISTLE') {
      kind = RefSignalKind.Whistle;
    } else if (e.type === 'FOUL' || e.type === 'PUNCH' || e.type === 'KICK') {
      kind = cardForSeverity(e.severity);
    }
    if (kind === RefSignalKind.None) continue;
    if (priority(kind) <= priority(bestKind)) continue;
    bestKind = kind;
    bestTick = e.tick;
    bestX = e.x ?? 0;
    bestZ = e.z ?? 0;
    bestPhase = e.phase ?? MatchPhase.Warmup;
  }

  if (bestKind === RefSignalKind.None) return false;
  out.kind = bestKind;
  out.tick = bestTick;
  out.x = bestX;
  out.z = bestZ;
  out.phase = bestPhase;
  return true;
}

/** Put a signal up (restarting its hold). A newer signal always wins — a red
 *  card mid-whistle is news, and the older cue has nothing left to say. */
export function raiseRefSignal(sig: RefSignal, r: RefSignalRaise): void {
  sig.kind = r.kind;
  sig.tick = r.tick;
  sig.x = r.x;
  sig.z = r.z;
  sig.phase = r.phase;
  sig.ageS = 0;
  sig.holdS = holdForKind(r.kind);
}

export function clearRefSignal(sig: RefSignal): void {
  sig.kind = RefSignalKind.None;
  sig.tick = -1;
  sig.ageS = 0;
  sig.holdS = 0;
}

/**
 * Advance the channel by one rendered frame and CLEAR it once the hold lapses.
 *
 * This is the whole reason the channel carries an age at all: unlike every other
 * `RenderState` field (which the shell defaults off each `beginFrame` and the
 * per-frame setters re-assert), a rule event arrives once. Nothing will ever
 * come along to turn it off, so the channel turns itself off.
 */
export function ageRefSignal(sig: RefSignal, dt: number): void {
  if (sig.kind === RefSignalKind.None) return;
  sig.ageS += dt > 0 ? dt : 0;
  if (sig.ageS >= sig.holdS) clearRefSignal(sig);
}

/**
 * Whistle PULSE envelope, 0..1 — a hard onset decaying to nothing over
 * WHISTLE_FLASH_S. 0 for a card, and 0 once the flash is spent even though the
 * referee is still walking out his WHISTLE_HOLD_S ceremony.
 */
export function whistleFlash(sig: RefSignal): number {
  if (sig.kind !== RefSignalKind.Whistle) return 0;
  const t = sig.ageS / WHISTLE_FLASH_S;
  if (t < 0 || t >= 1) return 0;
  // Squared falloff: bright instant, quick tail — reads as a burst, not a lamp.
  const k = 1 - t;
  return k * k;
}

/** Card plane opacity, 0..1 — fade in, hold, fade out. 0 for a whistle. */
export function cardOpacity(sig: RefSignal): number {
  if (sig.kind !== RefSignalKind.Yellow && sig.kind !== RefSignalKind.Red) return 0;
  const t = sig.ageS;
  if (t < 0 || t >= sig.holdS) return 0;
  if (t < CARD_FADE_IN_S) return t / CARD_FADE_IN_S;
  const remaining = sig.holdS - t;
  if (remaining < CARD_FADE_OUT_S) return remaining / CARD_FADE_OUT_S;
  return 1;
}

/** True while the referee should be walking to / standing over the spot. */
export function refCeremonyActive(sig: RefSignal): boolean {
  return sig.kind !== RefSignalKind.None;
}

// ---------------------------------------------------------------------------
// The feed. One helper every mount calls, so the four game loops (practice,
// solo, arena, online) never re-express the scan.
// ---------------------------------------------------------------------------

/** Structural — `Renderer` satisfies this. Kept narrow so a test can stub it. */
export interface RefSignalSink {
  signalReferee(kind: RefSignalKind, tick: number, x: number, z: number, phase: MatchPhase): void;
}

/**
 * Module-level scratch so `feedRefSignals` allocates nothing per tick (§15.7).
 * Safe because JS is single-threaded and the value is consumed synchronously
 * before this function returns — the same reasoning behind renderer.ts's
 * hoisted `lookAt` vector and main.ts's `camInput`.
 */
const feedScratch: RefSignalRaise = {
  kind: RefSignalKind.None,
  tick: -1,
  x: 0,
  z: 0,
  phase: MatchPhase.Warmup,
};

/**
 * Drain one tick's rule events into a renderer's referee channel.
 *
 * MUST be called once per SIM TICK from inside `driver.advance` (right after
 * `step()`), not once per rendered frame — `StepResult.events` is zeroed at the
 * top of the next `step()`, so a frame that ran several ticks would otherwise
 * only ever see the last one's. Exactly the placement `banners.pushEvents` and
 * `audio.update` already use, and for the same reason.
 *
 * Returns whether a signal was raised (handy for tests and for a caller that
 * wants to trigger something else off the same edge).
 */
export function feedRefSignals(events: readonly RuleEvent[], sink: RefSignalSink): boolean {
  if (events.length === 0) return false;
  if (!scanRefSignal(events, feedScratch)) return false;
  sink.signalReferee(feedScratch.kind, feedScratch.tick, feedScratch.x, feedScratch.z, feedScratch.phase);
  return true;
}
